import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

function classNames(...xs) {
    return xs.filter(Boolean).join(" ");
}

function useOutsideClick(ref, onOutside) {
    useEffect(() => {
        function handler(e) {
            if (!ref.current) return;
            if (!ref.current.contains(e.target)) onOutside();
        }
        window.addEventListener("mousedown", handler);
        return () => window.removeEventListener("mousedown", handler);
    }, [ref, onOutside]);
}

function MultiSelect({ options, value, onChange, placeholder = "Select...", maxHeight = 240, className }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [highlight, setHighlight] = useState(0);
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    useOutsideClick(wrapRef, () => setOpen(false));

    const selected = useMemo(() => {
        const map = new Map(options.map((o) => [o.value, o.label]));
        return value.map((v) => ({ value: v, label: map.get(v) ?? String(v) }));
    }, [value, options]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
    }, [options, query]);

    useEffect(() => {
        setHighlight((h) => Math.max(0, Math.min(h, Math.max(0, filtered.length - 1))));
    }, [filtered.length]);

    function toggle(valueToToggle) {
        if (value.includes(valueToToggle)) {
            onChange(value.filter((v) => v !== valueToToggle));
        } else {
            onChange([...value, valueToToggle]);
        }
    }

    function clearAll() {
        onChange([]);
        if (inputRef.current) inputRef.current.focus();
    }

    function onKeyDown(e) {
        if (!open && ["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
            setOpen(true);
            setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
        }

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(filtered.length - 1, h + 1));
            if (listRef.current) listRef.current.scrollTo({ top: (highlight + 1) * 36 - 144, behavior: "smooth" });
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
            if (listRef.current) listRef.current.scrollTo({ top: (highlight - 1) * 36 - 144, behavior: "smooth" });
        }
        if (e.key === "Enter") {
            e.preventDefault();
            const opt = filtered[highlight];
            if (opt) toggle(opt.value);
        }
        if (e.key === "Escape") setOpen(false);
        if (e.key === "Backspace" && query === "" && value.length > 0) {
            onChange(value.slice(0, -1));
        }
    }

    return (
        <div className={classNames("w-full", className)} ref={wrapRef}>
            {/* Control */}
            <div
                className={classNames(
                    "flex min-h-12 w-full flex-wrap items-center gap-1 rounded-2xl border border-gray-300 bg-white px-3 py-2 shadow-sm transition focus-within:ring-2 focus-within:ring-indigo-500",
                    open && "ring-2 ring-indigo-500"
                )}
                onClick={() => {
                    setOpen(true);
                    if (inputRef.current) inputRef.current.focus();
                }}
            >
                {/* Chips */}
                {selected.map((s) => (
                    <span key={s.value} className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-2 py-1 text-sm shadow-sm">
                        {s.label}
                        <button
                            className="rounded-full p-1 hover:bg-gray-100"
                            aria-label={`Remove ${s.label}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggle(s.value);
                            }}
                        >
                            ×
                        </button>
                    </span>
                ))}

                {/* Input */}
                <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={selected.length ? "" : placeholder}
                    className="flex-1 min-w-[8ch] border-none bg-transparent outline-none placeholder:text-gray-400"
                />

                {/* Clear button */}
                {value.length > 0 && (
                    <button
                        className="ml-auto rounded-full px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
                        onClick={(e) => {
                            e.stopPropagation();
                            clearAll();
                        }}
                    >
                        Clear
                    </button>
                )}

                {/* Chevron */}
                <span className="ml-1 select-none text-gray-500">▾</span>
            </div>

            {/* Dropdown */}
            {open && (
                <div className="relative z-10" role="listbox" aria-multiselectable="true">
                    <div
                        ref={listRef}
                        style={{ maxHeight }}
                        className="mt-2 w-full overflow-auto rounded-2xl border border-gray-200 bg-white shadow-lg"
                    >
                        {filtered.length === 0 ? (
                            <div className="px-3 py-3 text-sm text-gray-500">No results</div>
                        ) : (
                            filtered.map((o, i) => {
                                const isSelected = value.includes(o.value);
                                const isActive = i === highlight;
                                return (
                                    <button
                                        key={o.value}
                                        role="option"
                                        aria-selected={isSelected}
                                        onMouseEnter={() => setHighlight(i)}
                                        onClick={() => toggle(o.value)}
                                        className={classNames(
                                            "flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
                                            isActive ? "bg-indigo-50" : "",
                                            isSelected ? "font-medium" : ""
                                        )}
                                    >
                                        <input
                                            type="checkbox"
                                            readOnly
                                            checked={isSelected}
                                            className="pointer-events-none h-4 w-4 rounded border-gray-300"
                                        />
                                        <span>{o.label}</span>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

MultiSelect.propTypes = {
    options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired })
    ).isRequired,
    value: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    maxHeight: PropTypes.number,
    className: PropTypes.string,
};

export default MultiSelect;